import { differenceInDays, isToday } from "date-fns";
import "primeicons/primeicons.css";
import "primereact/resources/primereact.min.css";
import { Resizable } from "re-resizable";
import React, { useEffect, useRef, useState, lazy } from "react";
import { useDataContext } from "../../../hooks/useDataContext.js";
import Loading from "../../compound/Loader.js";
import ContentRowUnassigned from "./ContentRowUnassigned/Index.js";
import "./DashboardTable.scss";
import TableHeader from "./TableHeader/Index.js";
import "./TableHeader/TableHeader.scss";
import ContentRow from "./contentRow/Index.js";
import { isWeekend } from "../../../utils/calandarFns";

const MainTaskView = lazy(() => import("./MainTaskView/Index.js"));

const DashboardTable = ({ 
  data: propData, 
  unassignedData: propUnassignedData, 
  columnWidth: propColumnWidth, 
  filters: propFilters,
  dates: propDates,
  hideUnassigned = false,
  hideTimeline = false,
  onTaskClick = null,
  hideMainTaskView = false,
  calendarHeightIncrease = 0,
  hideUserName = false,
  hideCountryside = false
} = {}) => {
  const [showContent, setShowContent] = useState(false);
  const contextData = useDataContext();
  
  // Use props if provided, otherwise fallback to context (backward compatibility)
  const data = propData !== undefined ? propData : contextData?.data;
  const unassignedData = propUnassignedData !== undefined ? propUnassignedData : contextData?.unassignedData;
  const columnWidth = propColumnWidth !== undefined ? propColumnWidth : contextData?.columnWidth;
  const filters = propFilters !== undefined ? propFilters : contextData?.filters;
  const dates = propDates !== undefined ? propDates : contextData?.dates;
  const [maxHeight, setMaxHeight] = useState();
  const div1Ref = useRef(null);
  const div2Ref = useRef(null);
  const innerContentRef = useRef(null);
  const initialHeight =
    localStorage.getItem("tableRowHeight") || window.innerHeight - 200;
  const scrollToToday = () => {
    const parentDiv = div1Ref.current;
    const childDiv = document.querySelector(".today");
    if (filters?.startDate && columnWidth) {
      const getDayDiff = differenceInDays(new Date(), filters.startDate);
      if (childDiv) {
        parentDiv.scrollLeft += getDayDiff * columnWidth;
      }
    }
  };

  useEffect(() => {
    setShowContent(true);
  }, []);

  const handleScroll = (event) => {
    const { scrollLeft } = event.target;
    if (div2Ref.current && div1Ref.current) {
      div1Ref.current.scrollLeft = scrollLeft;
      div2Ref.current.scrollLeft = scrollLeft;
    }
  };

  const handleTableHeight = (event, direction, ref, d) => {
    // access the new height with ref.offsetHeight
    const newHeight = ref?.offsetHeight;
    localStorage.setItem("tableRowHeight", newHeight);
  };

  useEffect(() => {
    if (innerContentRef?.current) {
      let windowHieght = window.innerHeight - 100;
      setTimeout(() => {
        const contentHeight = innerContentRef.current?.clientHeight + 80;
        setMaxHeight(
          windowHieght > contentHeight ? contentHeight : windowHieght
        );
      }, 100);
    }
    scrollToToday();
  }, [showContent, filters, columnWidth]);

  // Check if content should be shown
  if (!showContent) {
    // If content should not be shown, return a Loading component
    return <Loading />;
  } else {
    // If content should be shown, render the main content
    return (
      <div>
        <div className="dashboard-table-wrapper">
          <Resizable
            defaultSize={{ height: Number(initialHeight), width: "100%" }}
            onResize={handleTableHeight}
            maxHeight={maxHeight}
            minHeight={100}
          >
            <div className="main-panel" ref={div1Ref} onScroll={handleScroll}>
              <div className="header-row">
                <TableHeader dates={dates} columnWidth={columnWidth} />
              </div>

              <div className="content-row test-content" ref={innerContentRef}>
                {!!data?.length &&
                  data.map((user) => (
                    <ContentRow key={user.userId} user={user} hideTimeline={hideTimeline} onTaskClick={onTaskClick} calendarHeightIncrease={calendarHeightIncrease} hideUserName={hideUserName} hideCountryside={hideCountryside} dates={dates} columnWidth={columnWidth} />
                  ))}
                {/* Render empty placeholder row when data is empty to show grid lines */}
                {(!data || !data.length) && dates && dates.length > 0 && (
                  <div className="row">
                    {!hideUserName && (
                      <div className="user-name"></div>
                    )}
                    <div className="year" style={{ minHeight: '500px' }}>
                      {dates.map((day, index) => (
                        <div
                          key={index}
                          className={`col ${isWeekend(day.date) ? "weekend" : ""} ${isToday(new Date(day.date)) ? 'today' : ''}`}
                          style={{ minWidth: `${columnWidth || 50}px` }}
                        ></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Resizable>

          {/* unassigned tasks */}
          {!hideUnassigned && (
            <div className="unassigned-table-wrap">
              <h3>Unassigned Tasks</h3>
              <div
                className="unassigned-table"
                ref={div2Ref}
                onScroll={handleScroll}
              >
                <div className="content-row">
                  {unassignedData &&
                    unassignedData.map((category) => (
                      <ContentRowUnassigned
                        key={category?.categoryId}
                        category={category}
                      />
                    ))}
                </div>
              </div>
            </div>
          )}

          {!hideMainTaskView && (
            <MainTaskView
              showContent={showContent}
              setShowContent={setShowContent}
            />
          )}
        </div>
      </div>
    );
  }
};

export default DashboardTable;
